import { Box, Stack, Text, Wrap } from '@chakra-ui/react';
import { setupI18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import { useLingui } from '@lingui/react/macro';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { domAnimation, LazyMotion } from 'motion/react';
import { useMemo, useState } from 'react';

import { DiscoverList } from '#modules/finder/features/posts/components/Discover/DiscoverList';
import { ActiveFilterChips } from '#modules/finder/features/posts/components/Filters/ActiveFilterChips';
import { GameSettingTag } from '#modules/finder/features/posts/components/GameSettingTag';
import { IncompleteSettingsNotice } from '#modules/finder/features/posts/components/IncompleteSettingsNotice';
import { Desktop } from '#modules/finder/features/posts/components/Post/Desktop';
import { Mobile } from '#modules/finder/features/posts/components/Post/Mobile';
import type { PostProps } from '#modules/finder/features/posts/components/Post/types';
import { FormActions } from '#modules/finder/features/posts/components/PostForm/FormActions';
import { UnavailableSettingsNotice } from '#modules/finder/features/posts/components/UnavailableSettingsNotice';
import snapshot from '#modules/finder/features/posts/model/archivedSettingsFixture.json';
import type { FinderFilterValues } from '#modules/finder/features/posts/model/finderFilters';
import type { Post } from '#modules/finder/features/posts/types';
import { getEditableSettingOptions } from '#modules/finder/features/posts/utils/getEditableSettingOptions';
import { GameSlotType } from '#features/api/types';
import { messages as enMessages } from '#features/i18n/locales/en/messages.po';
import { messages as ruMessages } from '#features/i18n/locales/ru/messages.po';
import { Button, Panel } from '#features/ui';
import { GameParameterSelect } from '#features/ui/components/inputs/GameParameterSelect';

/** Dev fixture only. All values and actions stay in this component's local state. */
export const Route = createFileRoute('/{-$locale}/_layout/preview/archived-settings')({
  ssr: false,
  beforeLoad: () => {
    if (!import.meta.env.DEV) throw notFound();
  },
  validateSearch: (search: Record<string, unknown>) => ({
    lang: search.lang === 'en' ? ('en' as const) : ('ru' as const),
    parameters:
      search.parameters === 'many' ||
      search.parameters === 'empty' ||
      search.parameters === 'single'
        ? search.parameters
        : 'short',
  }),
  component: PreviewRoute,
});

function PreviewRoute() {
  const { lang } = Route.useSearch();
  const i18n = useMemo(
    () => setupI18n({ locale: lang, messages: { en: enMessages, ru: ruMessages } }),
    [lang]
  );
  return (
    <I18nProvider i18n={i18n}>
      <Fixture key={lang} locale={lang} />
    </I18nProvider>
  );
}

function Fixture({ locale }: { locale: 'ru' | 'en' }) {
  const { t } = useLingui();
  const { parameters } = Route.useSearch();
  const [incomplete, setIncomplete] = useState(true);
  const [selected, setSelected] = useState<string | string[] | null>([
    'd18597b3-e6e7-47a6-81e9-b11d08b0627d',
    'cashout',
  ]);
  const [newSelected, setNewSelected] = useState<string | string[] | null>([]);
  const [filter, setFilter] = useState<FinderFilterValues>({
    gameSettings: { mode: ['d18597b3-e6e7-47a6-81e9-b11d08b0627d'] },
  });
  const title = { ru: 'Игровой режим', en: 'Game mode' }[locale];
  const [restored, setRestored] = useState(false);
  const [longLabel, setLongLabel] = useState(false);
  const active = [
    {
      id: 'cashout',
      name: 'Cashout',
      tagImage: 'https://media.dev.teamplay.gg/assets/Eww8/ZPV.png',
    },
    { id: 'ranked-cashout', name: 'Ranked Cashout' },
    {
      id: 'power-shift',
      name: 'Power Shift',
      tagImage: 'https://media.dev.teamplay.gg/assets/Eww8/OW1.png',
    },
  ];
  const saved = [
    {
      id: 'd18597b3-e6e7-47a6-81e9-b11d08b0627d',
      name: longLabel
        ? locale === 'ru'
          ? 'Соревновательный режим с длинным названием'
          : 'Competitive mode with a long descriptive name'
        : 'Bank It',
      unavailable: !restored,
      tagImage: 'https://media.dev.teamplay.gg/assets/FHBw/2am.png',
    },
  ];
  const available = restored ? [...active, ...saved] : active;
  const selectedIds = Array.isArray(selected) ? selected : selected ? [selected] : [];
  const hasUnavailable = !restored && selectedIds.includes(saved[0].id);
  const post = {
    ...snapshot.posts[0],
    id: 'history-fixture',
    activeSession: null,
    favourite: false,
    text:
      locale === 'ru'
        ? 'Ищу команду для спокойной игры вечером.'
        : 'Looking for a team for relaxed evening matches.',
    data: [
      {
        key: 'mode',
        title,
        multiple: true,
        previews: [GameSlotType.TAG],
        pages: [GameSlotType.TAG],
        socials: [GameSlotType.TAG],
        values:
          parameters === 'empty'
            ? []
            : parameters === 'single'
              ? [{ ...active[0], unavailable: false }]
              : parameters === 'many'
                ? [
                    { ...active[0], unavailable: false },
                    ...['Diamond', 'Europe', 'PC', 'Ranked', 'Support', 'Evening', 'Teamplay'].map(
                      (name) => ({ id: name, name, unavailable: false })
                    ),
                    { ...saved[0] },
                  ]
                : [{ ...active[0], unavailable: false }, { ...saved[0] }],
      },
    ],
  } as unknown as Post;
  const game = snapshot.games.find((item) => item.id === post.gameId) ?? snapshot.games[0];
  const pageProps = {
    game,
    userData: post.userData,
    post,
    tags: post.data?.[0]?.values ?? [],
    platforms: [{ id: 'pc', name: 'PC' }],
    isOwnPost: false,
    isAuth: false,
    myId: '',
    postId: post.id,
    canBump: false,
    updatedAgoInSeconds: 0,
    updatedTime: 0,
    onBumpPostClick: () => {},
    isBumpPostLoading: false,
    refreshPostData: () => {},
    onStartChatClick: () => {},
    isStartChatLoading: false,
  } as unknown as PostProps;
  const [client] = useState(() => {
    const catalog = new QueryClient({
      defaultOptions: { queries: { enabled: false, staleTime: Infinity } },
    });
    const defaults = catalog.defaultQueryOptions.bind(catalog);
    catalog.defaultQueryOptions = (options) => ({
      ...defaults(options),
      enabled: false,
      staleTime: Infinity,
      refetchInterval: false,
    });
    catalog.setQueryData(['games'], snapshot.games);
    catalog.setQueryData(['platforms'], [{ id: 'pc', name: 'PC' }]);
    catalog.setQueryData(['me'], null);
    catalog.setQueryData(
      ['gameSettings', 'archived-settings-fixture'],
      [{ key: 'mode', title, filterShown: true, multiple: true, values: active }]
    );
    return catalog;
  });
  return (
    <QueryClientProvider client={client}>
      <LazyMotion features={domAnimation}>
        <Stack p={4} gap={6} maxW="1120px" mx="auto" data-testid="history-values-preview">
          <Wrap gap={3}>
            <Button onClick={() => setRestored(!restored)}>
              {restored ? t`Hide option` : t`Restore option`}
            </Button>
            <Button onClick={() => setLongLabel(!longLabel)}>{t`Long labels`}</Button>
          </Wrap>
          <Box maxW="440px" w="full" data-testid="history-post-card">
            <DiscoverList
              items={[
                {
                  ...post,
                  discordMembersCount: 0,
                  discordPublished: false,
                  bookmarkDisabled: true,
                },
              ]}
              isLoading={false}
              loadMore={() => {}}
              restorationKey="history-parameters-preview"
            />
          </Box>
          <Wrap gap={2} data-testid="history-setting-tags">
            <GameSettingTag {...saved[0]} />
            <GameSettingTag {...active[0]} unavailable={false} />
          </Wrap>
          <Box data-testid="history-post-page">
            <Box hideBelow="lg">
              <Desktop {...pageProps} />
            </Box>
            <Box hideFrom="lg">
              <Mobile {...pageProps} />
            </Box>
          </Box>
          <Panel variant="object" p={4} data-testid="history-incomplete-settings">
            <Stack gap={3}>
              {incomplete ? (
                <IncompleteSettingsNotice onRetry={() => setIncomplete(false)} />
              ) : null}
              <FormActions submitLabel={t`common:form.save`} isDisabled={incomplete} />
            </Stack>
          </Panel>
          <Panel variant="object" p={4}>
            <Stack gap={3}>
              <Text textStyle="title">{t`Edit post`}</Text>
              {hasUnavailable ? <UnavailableSettingsNotice /> : null}
              <GameParameterSelect
                name="mode"
                label={title}
                value={selected}
                onChange={(value) => {
                  setSelected(
                    Array.isArray(value)
                      ? value.filter((id): id is string => typeof id === 'string')
                      : typeof value === 'string'
                        ? value
                        : null
                  );
                }}
                multiple
                clearable
                maxCount={3}
                options={getEditableSettingOptions(available, saved).map((option) => ({
                  ...option,
                  secondaryText: option.disabled ? t`No longer available` : undefined,
                }))}
              />
            </Stack>
          </Panel>
          <Panel variant="object" p={4}>
            <Stack gap={3}>
              <Text textStyle="title">{t`post:form.publishListing`}</Text>
              <GameParameterSelect
                name="new-mode"
                label={title}
                value={newSelected}
                onChange={(value) => {
                  setNewSelected(
                    Array.isArray(value)
                      ? value.filter((id): id is string => typeof id === 'string')
                      : typeof value === 'string'
                        ? value
                        : null
                  );
                }}
                multiple
                clearable
                maxCount={3}
                options={getEditableSettingOptions(available, [])}
              />
            </Stack>
          </Panel>
          <Panel variant="object" p={4}>
            <ActiveFilterChips
              gameId="archived-settings-fixture"
              values={filter}
              onUpdate={(next) => setFilter(next as FinderFilterValues)}
            />
          </Panel>
        </Stack>
      </LazyMotion>
    </QueryClientProvider>
  );
}
